<template>
  <div v-if="showTiles">
    <div v-if="folders.length === 0" class="folders-empty-card">
      <b-icon icon="folder-fill" font-scale="2.5" class="empty-icon" />
      <h5 class="font-weight-bold mt-2">No folders yet</h5>
      <p class="empty-text">
        Create folders to organise your files and locate them with ease.
      </p>
      <b-button
        variant="primary"
        class="mr-2"
        @click="$emit('suggest-folders')"
      >
        Add suggested folders
      </b-button>
      <b-button variant="outline-secondary" @click="$emit('new-folder')">
        + New folder
      </b-button>
    </div>

    <div v-else class="folder-row pt-2">
      <b-button
        :key="folder.id"
        v-for="folder in tiles"
        class="folder-tile"
        @click="selectFolder(folder.id)"
      >
        <b-icon
          icon="folder-fill"
          font-scale="1.2"
          class="tile-icon"
          :style="iconStyle(folder.color)"
        />
        <span class="tile-text">
          <span class="tile-name">{{ folder.label }}</span>
          <span class="tile-count">
            {{ folder.count }} {{ folder.count === 1 ? 'file' : 'files' }}
          </span>
        </span>
      </b-button>
    </div>
  </div>
</template>

<script>
import { folderIconStyle } from '@/utils/folderColors'

export default {
  props: {
    selectedFolder: { type: String, default: 'all' },
    folders: { type: Array, default: () => [] },
    canManage: { type: Boolean, default: true },
  },

  computed: {
    // On All files the top-level folders; inside a folder, the folders in it.
    tiles() {
      const parent = this.selectedFolder === 'all' ? null : this.selectedFolder
      return this.folders.filter(
        (folder) => (folder.parentId || null) === parent,
      )
    },

    showTiles() {
      if (this.selectedFolder === 'all') {
        return this.canManage || this.folders.length > 0
      }
      return this.tiles.length > 0
    },
  },

  methods: {
    iconStyle: folderIconStyle,

    selectFolder(folderId) {
      this.$emit('folder-change', folderId)
    },
  },
}
</script>

<style scoped>
.folder-tile {
  display: flex;
  align-items: center;
  padding: 0 12px;
  background-color: #ffffff;
  border-color: #dcdddd;
  border-radius: 10px;
  color: #000000;
  font-weight: bold;
  text-align: left;
}

.tile-icon {
  flex-shrink: 0;
  margin-right: 10px;
  color: #006cff;
}

.tile-text {
  min-width: 0;
  line-height: 1.25;
}

.tile-name,
.tile-count {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tile-count {
  color: #6b7280;
  font-size: 13px;
  font-weight: 400;
}

.folder-tile:hover {
  background-color: #ffffff;
  border-color: #1a60a7;
  color: #000000;
}

.folder-row {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 24px;
}

/* Below md the folders are a compact list, so a long one needn't be scrolled sideways. */
@media (max-width: 767.98px) {
  .folder-row {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .folder-row .folder-tile {
    width: 100%;
    height: 56px;
  }
}

.folders-empty-card {
  margin-bottom: 16px;
  padding: 28px 20px;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  text-align: center;
}

.empty-icon {
  color: #006cff;
}

.empty-text {
  max-width: 520px;
  margin: 0 auto 16px;
  color: #6b7280;
}
</style>
